/**
 * startupSweep — cursor-based scan of `$totodo.doc_updates` for documents
 * that changed since the last session.
 *
 * Uses a localStorage cursor (`totodo:doc-sweep-cursor`) to resume from where
 * we left off. Only scans Y.Doc update rows that are *new* since the cursor,
 * extracts unique task IDs, and feeds them to docProcessor.markManyDirty().
 *
 * Why cursor-based instead of full-table scan:
 * - Only 674 tasks have Y.Docs vs 4,227+ tasks total → 6× less work
 * - Incremental: after first session, only new/changed docs are scanned
 * - Uses the auto-increment `i` index on `$totodo.doc_updates` → ordered scan
 *
 * Progressive cursor: saved to localStorage after each batch is processed
 * by the docProcessor, so a crash mid-sweep only re-processes the current batch.
 *
 * @see docProcessor.ts for the processing pipeline
 * @see index.ts for the orchestration
 */

import { db } from "@/db/schema"
import { docProcessor } from "./docProcessor"

const CURSOR_KEY = "totodo:doc-sweep-cursor"

/** Read the persisted cursor (0 if first run). */
function getCursor(): number {
  try {
    const raw = localStorage.getItem(CURSOR_KEY)
    return raw ? Number(raw) || 0 : 0
  } catch {
    return 0
  }
}

/** Persist the cursor position. Called after each batch is processed. */
export function saveCursor(cursor: number): void {
  try {
    localStorage.setItem(CURSOR_KEY, String(cursor))
  } catch {
    // localStorage may be full or unavailable — non-fatal
  }
}

/**
 * Scan `$totodo.doc_updates` from the saved cursor, collect unique task IDs,
 * and feed them to the docProcessor.
 */
export async function sweepStaleDocuments(): Promise<void> {
  try {
    const cursor = getCursor()
    const updatesTable = db.table("$totodo.doc_updates")

    // Collect unique task IDs and track the highest auto-increment seen
    const uniqueIds = new Set<string>()
    let maxI = cursor

    await updatesTable
      .where("i")
      .above(cursor)
      .each((row: { i: number; k: string }) => {
        if (row.k) uniqueIds.add(row.k)
        if (row.i > maxI) maxI = row.i
      })

    if (uniqueIds.size === 0) {
      console.info("[startupSweep] No new Y.Doc updates since last session")
      // Still save cursor in case the table was compacted
      if (maxI > cursor) saveCursor(maxI)
      return
    }

    console.info(
      `[startupSweep] Found ${uniqueIds.size} documents with Y.Doc updates (cursor ${cursor} → ${maxI})`,
    )

    const ids = Array.from(uniqueIds)
    docProcessor.markManyDirty(ids)

    // Save cursor after feeding to processor
    // (if we crash before processing completes, cursor-sweep will
    //  re-scan these same IDs next startup — safe because skip-if-unchanged)
    saveCursor(maxI)
  } catch (error) {
    console.error(
      "[startupSweep] Sweep failed:",
      error instanceof Error
        ? error.message + "\n" + error.stack
        : JSON.stringify(error),
    )
  }
}
